import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { useNavigate, useParams } from "react-router-dom";
import StatusMessage from "./status-message";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import { getPartnerDetailsSagaAction } from "../../../../redux/saga/partner-details/saga-actions";
import { addExternalContractSagaAction } from "../../../../redux/saga/external-contract/saga-actions";
import ExternalContractForm from "../external-contract-form";
import { Constants } from "../../../../utils/constants";
import "./CreateExternalContract.scss";

const CreateExternalContract = () => {
  const [openAccordion, setOpenAccordion] = React.useState<number>(1);
  const [contractDetails, setContractDetails] = React.useState({
    companyName: "",
    salesforceAccount: "",
    accountManagerEmail: "",
    oracleChannel: "",
    country: "",
    region: Constants.REGIONS[0],
    soNumber: "",
    salesforceOpportunity: "",
    typeOfEngagement: "",
    revenueStream: "",
    workPackageType: "",
    workPackageAmount: "",
    overallExpiryDate: null,
    supportExpiryDate: null,
    startDate: null,
  });
  const [formSubmitted, setFormSubmitted] = React.useState<boolean>(false);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { workPackages } = useAppSelector((state) => state.external_contract);
  const { partnerID } = useParams();
  const numberID = Number(partnerID);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const selectedWorkPackageType = workPackages.find(
    (workPackage) => workPackage.value === contractDetails.workPackageType
  );

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
    if (!partnerDetails || numberID !== partnerDetails.ID)
      dispatch(getPartnerDetailsSagaAction(numberID));
  }, []);

  React.useEffect(() => {
    if (partnerDetails && partnerDetails.ID === numberID) {
      setContractDetails({
        ...contractDetails,
        companyName: partnerDetails.partner_name,
        accountManagerEmail: partnerDetails.account_manager,
        country: partnerDetails.country,
        revenueStream: partnerDetails.revenue_stream,
        region: partnerDetails.region
      });
    }
  }, [partnerDetails]);

  const formatDateForDB = (date: Date | null) => {
    if (date) {
      const formattedDate = date.toLocaleDateString("en-CA");
      return formattedDate;
    }
    return null;
  };

  const onCreateClick = () => {
    const contractStatus =
      new Date() <= contractDetails.overallExpiryDate ? "Active" : "Inactive";
    
    const submitData = {
      externalContract: {
        company_name: contractDetails.companyName,
        salesforce_account: contractDetails.salesforceAccount,
        account_manager: contractDetails.accountManagerEmail,
        status: contractStatus,
        oracle_channel: Number(contractDetails.oracleChannel),
        country: contractDetails.country,
        region: contractDetails.region,
        so_number: Number(contractDetails.soNumber),
        salesforce_opportunity: contractDetails.salesforceOpportunity,
        type_of_engagement: contractDetails.typeOfEngagement,
        revenue_stream: contractDetails.revenueStream,
        contract_name: contractDetails.workPackageType,
        contract_amount: contractDetails.workPackageAmount,
        end_date: formatDateForDB(contractDetails.overallExpiryDate),
        support_expiry_date: formatDateForDB(contractDetails.supportExpiryDate),
        start_date: formatDateForDB(contractDetails.startDate),
        collection: selectedWorkPackageType?.metadata?.collection,
        sku: selectedWorkPackageType?.metadata?.part_number,
      },
      ID: partnerDetails.ID,
    };
    dispatch(addExternalContractSagaAction(submitData));
    setFormSubmitted(true);
  };

  const isNextButtonEnabled =
    contractDetails.companyName &&
    emailRegex.test(contractDetails.accountManagerEmail) &&
    contractDetails.region &&
    contractDetails.country;

  const isSaveButtonEnabled =
    contractDetails.revenueStream &&
    contractDetails.workPackageType &&
    contractDetails.workPackageAmount &&
    contractDetails.startDate &&
    contractDetails.overallExpiryDate &&
    (contractDetails.supportExpiryDate ||
      !selectedWorkPackageType?.metadata.support_expiry);

  return (
    <div className="create-external-contract-container">
      {formSubmitted ? (
        <StatusMessage ID={numberID} />
      ) : (
        <>
          <div className="form-title">Create External Contract</div>
          <div className="accordion-no-1">
            <div className="accordion-title">
              <Iconography icon="ballot" size={IconographySizes.Medium} />
              <p>General & Sales Information</p>
            </div>
            {openAccordion === 1 && (
              <>
                <div className="accordion-body">
                  <ExternalContractForm
                    contractDetails={contractDetails}
                    setContractDetails={setContractDetails}
                    formPage="General & Sales Information"
                  />
                  <div className="button-group">
                    <Button
                      variant="secondary"
                      label="Cancel"
                      onClick={() => {
                        navigate(
                          `/hid-admin/dashboard/partner-organizations/${partnerID}/services-and-subscriptions`,
                          { state: { selectedTab: "manage-contracts" } }
                        );
                      }}
                    />
                    <Button
                      variant="primary"
                      label="Next"
                      disabled={!isNextButtonEnabled}
                      onClick={() => {
                        setOpenAccordion(2);
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="accordion-no-2">
            <div
              className={`accordion-title ${
                openAccordion < 2 ? "disabled" : ""
              }`}
            >
              <Iconography icon="addressBook" size={IconographySizes.Medium} />
              <p>Engagement & Expiry</p>
            </div>
            {openAccordion === 2 && (
              <div className="accordion-body">
                <ExternalContractForm
                  contractDetails={contractDetails}
                  setContractDetails={setContractDetails}
                  formPage="Engagement & Expiry"
                />
                <div className="button-group">
                  <Button
                    variant="secondary"
                    label="Back"
                    onClick={() => {
                      setOpenAccordion(1);
                    }}
                  />
                  <Button
                    variant="primary"
                    label="Create"
                    disabled={!isSaveButtonEnabled}
                    onClick={() => {
                      onCreateClick();
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CreateExternalContract;
